import { createContext, useContext, useEffect, useState } from 'react';
import { User } from '@supabase/supabase-js';
import { supabase } from './supabase';

const DEMO_USER_KEY = 'immoiq_demo_mode';

const DEMO_USER = {
  id: 'demo-user-id',
  email: 'demo@immoiq.de',
  created_at: new Date().toISOString(),
  app_metadata: {},
  user_metadata: {},
  aud: 'authenticated',
  role: 'authenticated'
} as unknown as User;

const DEMO_CUSTOMER_DATA = {
  id: 'demo-customer-id',
  auth_user_id: 'demo-user-id',
  vorname: 'Demo',
  nachname: 'Nutzer',
  email: 'demo@immoiq.de',
  investor_type: 'Vermögens-Builder',
  wizard_completed: true,
  is_beta_user: false,
  beta_status: null,
};

export function isDemoMode() {
  return localStorage.getItem(DEMO_USER_KEY) === 'true';
}

export function enableDemoMode() {
  localStorage.setItem(DEMO_USER_KEY, 'true');
}

export function disableDemoMode() {
  localStorage.removeItem(DEMO_USER_KEY);
}

type AuthContextType = {
  user: User | null;
  loading: boolean;
  customerData: any;
  signOut: () => Promise<void>;
  signUp: (email: string, password: string) => Promise<any>;
  signIn: (email: string, password: string) => Promise<any>;
};

export const AuthContext = createContext<AuthContextType>({
  user: null,
  loading: true,
  customerData: null,
  signOut: async () => {},
  signUp: async () => ({ data: null, error: null }),
  signIn: async () => ({ data: null, error: null })
});

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(() => isDemoMode() ? DEMO_USER : null);
  const [customerData, setCustomerData] = useState<any>(() => isDemoMode() ? DEMO_CUSTOMER_DATA : null);
  const [loading, setLoading] = useState(() => !isDemoMode());

  useEffect(() => {
    // Demo mode bypass - state already set synchronously, just skip Supabase
    if (isDemoMode()) {
      return;
    }

    // Check active sessions and sets the user
    supabase.auth.getSession().then(({ data: { session } }) => {
      setUser(session?.user ?? null);
      if (session?.user) {
        fetchCustomerData(session.user.id);
      } else {
        setLoading(false);
      }
    });

    // Listen for changes on auth state (sign in, sign out, etc.)
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setUser(session?.user ?? null);
      if (session?.user) {
        fetchCustomerData(session.user.id);
      } else {
        setCustomerData(null);
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  const fetchCustomerData = async (authUserId: string) => {
    try {
      const { data, error } = await supabase
        .from('kunden')
        .select('*')
        .eq('auth_user_id', authUserId)
        .single();

      if (error) throw error;
      setCustomerData(data);
    } catch (error) {
      console.error('Error fetching customer data:', error);
    } finally {
      setLoading(false);
    }
  };

  const signUp = async (email: string, password: string) => {
    const result = await supabase.auth.signUp({
      email,
      password,
    });
    return result;
  };

  const signIn = async (email: string, password: string) => {
    const result = await supabase.auth.signInWithPassword({
      email,
      password,
    });
    return result;
  };

  const signOut = async () => {
    try {
      disableDemoMode();
      await supabase.auth.signOut();
      setUser(null);
      setCustomerData(null);
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  return (
    <AuthContext.Provider value={{ user, loading, customerData, signOut, signUp, signIn }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};